/**
 * Theme Provider
 *
 * React Context provider for theme management with support for light, dark,
 * and system preference modes. Persists user preference to localStorage.
 *
 * Validates: Requirements 1.5, 17.7
 * - Requirement 1.5: THE Console SHALL support a dark mode theme that can be
 *   toggled by the user.
 * - Requirement 17.7: THE Demo_Chat_Interface SHALL support light and dark themes
 *   based on user preference or system settings.
 */

import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

/** The actual resolved theme (what is displayed) */
export type Theme = 'light' | 'dark';

/** The user's theme preference (includes system option) */
export type ThemeMode = 'light' | 'dark' | 'system';

/** localStorage key for persisting theme preference */
const THEME_STORAGE_KEY = 'theme-preference';

/** Media query for detecting system dark mode preference */
const DARK_MODE_MEDIA_QUERY = '(prefers-color-scheme: dark)';

export interface ThemeContextValue {
  /** The current resolved theme (light or dark) */
  theme: Theme;
  /** The user's theme mode preference (light, dark, or system) */
  themeMode: ThemeMode;
  /** Set the theme mode preference */
  setTheme: (mode: ThemeMode) => void;
  /** Toggle between light and dark modes (skips system) */
  toggleTheme: () => void;
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Get the system's preferred color scheme
 */
function getSystemTheme(): Theme {
  if (typeof window === 'undefined') {
    return 'light';
  }
  return window.matchMedia(DARK_MODE_MEDIA_QUERY).matches ? 'dark' : 'light';
}

/**
 * Get the stored theme preference from localStorage
 */
function getStoredThemeMode(): ThemeMode {
  if (typeof window === 'undefined') {
    return 'system';
  }
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored;
  }
  return 'system';
}

/**
 * Resolve the actual theme from the theme mode
 */
function resolveTheme(mode: ThemeMode): Theme {
  if (mode === 'system') {
    return getSystemTheme();
  }
  return mode;
}

export interface ThemeProviderProps {
  children: React.ReactNode;
  /** Default theme mode if no preference is stored */
  defaultThemeMode?: ThemeMode;
}

/**
 * ThemeProvider component that manages theme state and provides context
 * to child components.
 *
 * Features:
 * - Supports light, dark, and system theme modes
 * - Persists preference to localStorage
 * - Listens to system preference changes when in 'system' mode
 * - Applies theme class to document root for CSS styling
 */
export function ThemeProvider({
  children,
  defaultThemeMode = 'system',
}: ThemeProviderProps) {
  // Initialize theme mode from localStorage or default
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    const stored = getStoredThemeMode();
    // If nothing stored, use the default
    if (stored === 'system' && defaultThemeMode !== 'system') {
      return defaultThemeMode;
    }
    return stored;
  });

  // Track the resolved theme (what's actually displayed)
  const [theme, setTheme] = useState<Theme>(() => resolveTheme(themeMode));

  // Update resolved theme when mode changes
  useEffect(() => {
    setTheme(resolveTheme(themeMode));
  }, [themeMode]);

  // Listen to system preference changes when in 'system' mode
  useEffect(() => {
    if (themeMode !== 'system') {
      return;
    }

    const mediaQuery = window.matchMedia(DARK_MODE_MEDIA_QUERY);

    const handleChange = (event: MediaQueryListEvent) => {
      setTheme(event.matches ? 'dark' : 'light');
    };

    // Modern browsers
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [themeMode]);

  // Apply theme class to document root
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    root.setAttribute('data-theme', theme);
  }, [theme]);

  // Persist theme preference to localStorage
  const handleSetTheme = useCallback((mode: ThemeMode) => {
    setThemeMode(mode);
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  }, []);

  // Toggle between light and dark (skips system)
  const toggleTheme = useCallback(() => {
    const newTheme: Theme = theme === 'light' ? 'dark' : 'light';
    handleSetTheme(newTheme);
  }, [theme, handleSetTheme]);

  const contextValue = useMemo<ThemeContextValue>(
    () => ({
      theme,
      themeMode,
      setTheme: handleSetTheme,
      toggleTheme,
    }),
    [theme, themeMode, handleSetTheme, toggleTheme]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}
